import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PRODUCTS, CATEGORIES } from '../data/mock';
import ProductCard from '../components/ProductCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || 'all';
  const initialQ = searchParams.get('q') || '';

  const [category, setCategory] = useState(initialCat);
  const [sortBy, setSortBy] = useState('featured');
  const [query, setQuery] = useState(initialQ);

  useEffect(() => {
    setCategory(searchParams.get('cat') || 'all');
    setQuery(searchParams.get('q') || '');
  }, [searchParams]);

  const filtered = useMemo(() => {
    let arr = [...PRODUCTS];
    if (category !== 'all') arr = arr.filter((p) => p.category === category);
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter((p) => p.name.toLowerCase().includes(q) || p.short.toLowerCase().includes(q));
    }
    if (sortBy === 'price-asc') arr.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-desc') arr.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [category, query, sortBy]);

  const updateCategory = (val) => {
    setCategory(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'all') next.delete('cat');
    else next.set('cat', val);
    setSearchParams(next);
  };

  return (
    <div className="bg-white">
      {/* Page header */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">Shop All Products</h1>
          <p className="mt-3 text-slate-600">Explore our diverse collection and find your next favorite item.</p>
          <p data-testid="catalog-total-count" className="mt-2 text-sm text-slate-500">
            {PRODUCTS.length} products across {CATEGORIES.length} categories
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8">
          <div className="flex-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products..."
              className="max-w-md"
            />
          </div>
          <div className="flex gap-3">
            <Select value={category} onValueChange={updateCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mb-6 text-sm text-slate-600" data-testid="catalog-filtered-count">
          Showing <span className="font-semibold text-slate-900">{filtered.length}</span> of {PRODUCTS.length} products
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-600">No products match your search.</p>
            <Button
              onClick={() => { setQuery(''); updateCategory('all'); }}
              className="mt-4 bg-[#5d728c] hover:bg-[#4d6278]"
            >
              Reset Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
